import { BoardState, GameState, PlayerState } from "../../../core/protocol/types";
import type {
  MultiplayerOverlayState,
  MultiplayerPhase,
} from "./MultiplayerPhaseTypes";
import { mapPhaseState } from "./MultiplayerPhaseMapper";

export interface MultiplayerStatsSnapshot {
  score: number;
  level: number;
  lines: number;
}

export interface PlayerBoardView {
  player: PlayerState;
  board: BoardState;
  isLocal: boolean;
  isActive: boolean;
}

export interface LobbySnapshot {
  players: PlayerState[];
  maxPlayers?: number;
}

export interface ResultsSnapshot {
  players: PlayerState[];
  roundNumber?: number;
}

export interface MultiplayerViewState {
  phase: MultiplayerPhase;
  runtimeReady: boolean;
  countdownNumber: number | null;
  overlay: MultiplayerOverlayState;
  stats: MultiplayerStatsSnapshot;
  boards: {
    local?: PlayerBoardView;
    remotes: PlayerBoardView[];
  };
  lobby?: LobbySnapshot;
  results?: ResultsSnapshot;
  rawGameState: GameState | null;
  errors: string[];
}

export type {
  MultiplayerPhase,
  MultiplayerOverlayState,
  MultiplayerOverlayType,
} from "./MultiplayerPhaseTypes";

const DEFAULT_STATS: MultiplayerStatsSnapshot = {
  score: 0,
  level: 1,
  lines: 0,
};

export function deriveCountdownNumber(gameState: GameState | null): number | null {
  if (!gameState || typeof gameState.countdown !== "number") {
    return null;
  }

  const { countdown } = gameState;
  if (countdown === 0 || countdown === 500) {
    return null;
  }

  const countdownNumber = Math.floor(countdown / 100) + 1;
  if (countdownNumber >= 1 && countdownNumber <= 5) {
    return countdownNumber;
  }

  return null;
}

export function createInitialViewState(): MultiplayerViewState {
  return {
    phase: "loading",
    runtimeReady: false,
    countdownNumber: null,
    overlay: { type: "loading", message: "Preparing runtime…" },
    stats: DEFAULT_STATS,
    boards: { remotes: [] },
    rawGameState: null,
    errors: [],
  };
}

interface BuildViewStateOptions {
  gameState: GameState | null;
  runtimeReady: boolean;
  stats?: MultiplayerStatsSnapshot;
  errors?: string[];
}

export function buildViewState({
  gameState,
  runtimeReady,
  stats,
  errors,
}: BuildViewStateOptions): MultiplayerViewState {
  const countdownNumber = deriveCountdownNumber(gameState);
  const { phase, overlay } = mapPhaseState({
    gameState,
    runtimeReady,
    countdownNumber,
  });
  const boards = deriveBoards(gameState);

  return {
    phase,
    runtimeReady,
    countdownNumber,
    overlay,
    stats: stats ?? DEFAULT_STATS,
    boards,
    lobby: deriveLobby(gameState),
    results: deriveResults(gameState),
    rawGameState: gameState,
    errors: errors ?? [],
  };
}

function deriveBoards(
  gameState: GameState | null
): { local?: PlayerBoardView; remotes: PlayerBoardView[] } {
  if (!gameState) {
    return { remotes: [] };
  }

  const remotes: PlayerBoardView[] = [];
  let local: PlayerBoardView | undefined;

  gameState.players.forEach((player) => {
    const view: PlayerBoardView = {
      player,
      board: player.board,
      isLocal: player.connectionId === gameState.localConnectionId,
      isActive: player.isActive,
    };

    if (view.isLocal) {
      local = view;
    } else {
      remotes.push(view);
    }
  });

  remotes.sort((a, b) => a.player.numPlayer - b.player.numPlayer);

  return { local, remotes };
}

function deriveLobby(gameState: GameState | null): LobbySnapshot | undefined {
  if (!gameState || gameState.status !== "waiting") {
    return undefined;
  }

  return {
    players: Array.from(gameState.players.values()),
    maxPlayers: gameState.maxPlayers,
  };
}

function deriveResults(gameState: GameState | null): ResultsSnapshot | undefined {
  if (!gameState || (gameState.status !== "finished" && gameState.roundSyncState !== "last")) {
    return undefined;
  }

  return {
    players: Array.from(gameState.players.values()),
    roundNumber: gameState.roundNumber,
  };
}

